if (typeof ArticleFaqFromContent !== 'function') {
  class ArticleFaqFromContent extends HTMLElement {
    // Any of these, matched as a whole heading's trimmed/lower-cased text
    // (trailing ':' or '?' ignored), marks the FAQ section title.
    static TITLE_MATCHES = ['frequently asked questions', 'faqs', 'faq'];
    static HEADING_TAGS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
    static START_MARKER = /^<===\s*start faq\s*===>$/i;
    static END_MARKER = /^<===\s*end faq\s*===>$/i;

    connectedCallback() {
      const content = this.querySelector('.kt-article-content') || this;

      const built = this.buildFromMarkers(content);
      if (built) return;

      // No explicit <=== START FAQ ===> / <=== END FAQ ===> markers found —
      // fall back to guessing the section's end from heading levels.
      const title = this.findFaqTitle(content);
      if (!title) return;

      const items = this.collectFaqItems(title);
      if (!items.length) return;

      const faqSection = this.buildFaqSection(title.textContent.trim(), items);
      title.replaceWith(faqSection);
    }

    // Explicit-boundary path: everything between a paragraph matching
    // "<=== START FAQ ===>" and one matching "<=== END FAQ ===>" is the FAQ
    // section, full stop — no heading-level guessing, so content after the
    // FAQ (regardless of its own heading level) is never swallowed.
    buildFromMarkers(content) {
      let start = null;
      let end = null;
      for (const el of content.children) {
        const text = el.textContent.trim();
        if (!start && ArticleFaqFromContent.START_MARKER.test(text)) {
          start = el;
        } else if (start && !end && ArticleFaqFromContent.END_MARKER.test(text)) {
          end = el;
          break;
        }
      }
      if (!start || !end) return false;

      const between = [];
      let node = start.nextElementSibling;
      while (node && node !== end) {
        between.push(node);
        node = node.nextElementSibling;
      }

      const titleIndex = between.findIndex(el => this.isFaqTitle(el));
      const titleText = titleIndex === -1 ? 'FAQs' : between[titleIndex].textContent.trim();
      const itemNodes = titleIndex === -1 ? between : between.slice(titleIndex + 1);

      const items = this.parseQaPairs(itemNodes);
      const faqSection = this.buildFaqSection(titleText, items);

      start.replaceWith(faqSection);
      between.forEach(n => n.remove());
      end.remove();
      return true;
    }

    parseQaPairs(nodes) {
      const items = [];
      const firstHeadingIndex = nodes.findIndex(n => ArticleFaqFromContent.HEADING_TAGS.includes(n.tagName));
      if (firstHeadingIndex === -1) return items;
      const questionLevel = Number(nodes[firstHeadingIndex].tagName[1]);

      let i = firstHeadingIndex;
      while (i < nodes.length) {
        const node = nodes[i];
        const isQuestion = ArticleFaqFromContent.HEADING_TAGS.includes(node.tagName) &&
          Number(node.tagName[1]) === questionLevel;

        if (!isQuestion) {
          i += 1;
          continue;
        }

        const question = node.textContent.trim();
        const answerNodes = [];
        let j = i + 1;
        while (
          j < nodes.length &&
          !(ArticleFaqFromContent.HEADING_TAGS.includes(nodes[j].tagName) &&
            Number(nodes[j].tagName[1]) <= questionLevel)
        ) {
          answerNodes.push(nodes[j]);
          j += 1;
        }

        items.push({ question, answerHtml: answerNodes.map(n => n.outerHTML).join('') });
        i = j;
      }

      return items;
    }

    isFaqTitle(el) {
      if (!ArticleFaqFromContent.HEADING_TAGS.includes(el.tagName)) return false;
      const text = el.textContent.trim().replace(/[:?]+$/, '').toLowerCase();
      return ArticleFaqFromContent.TITLE_MATCHES.includes(text);
    }

    findFaqTitle(content) {
      const headings = content.querySelectorAll(
        ArticleFaqFromContent.HEADING_TAGS.join(',')
      );
      for (const heading of headings) {
        if (this.isFaqTitle(heading)) return heading;
      }
      return null;
    }

    collectFaqItems(title) {
      const titleLevel = Number(title.tagName[1]);
      const items = [];
      const nodesToRemove = [];

      // First heading right after the title sets the "question" tag level
      // (e.g. H3 if the FAQ title itself is an H2).
      let node = title.nextElementSibling;
      while (
        node &&
        !(ArticleFaqFromContent.HEADING_TAGS.includes(node.tagName) &&
          Number(node.tagName[1]) > titleLevel)
      ) {
        node = node.nextElementSibling;
      }
      if (!node) return items;
      const questionLevel = Number(node.tagName[1]);

      while (node) {
        const isHeading = ArticleFaqFromContent.HEADING_TAGS.includes(node.tagName);
        const headingLevel = isHeading ? Number(node.tagName[1]) : null;

        // A heading at or above the FAQ title's own level ends the section.
        if (isHeading && headingLevel <= titleLevel) break;

        if (isHeading && headingLevel === questionLevel) {
          const question = node.textContent.trim();
          nodesToRemove.push(node);

          const answerNodes = [];
          let sibling = node.nextElementSibling;
          while (
            sibling &&
            !(ArticleFaqFromContent.HEADING_TAGS.includes(sibling.tagName) &&
              Number(sibling.tagName[1]) <= questionLevel)
          ) {
            answerNodes.push(sibling);
            sibling = sibling.nextElementSibling;
          }
          nodesToRemove.push(...answerNodes);

          items.push({ question, answerHtml: answerNodes.map(n => n.outerHTML).join('') });
          node = sibling;
          continue;
        }

        node = node.nextElementSibling;
      }

      nodesToRemove.forEach(n => n.remove());
      return items;
    }

    buildFaqSection(titleText, items) {
      const section = document.createElement('div');
      section.className = 'kt-journal-faq';

      const heading = document.createElement('h2');
      heading.className = 'kt-journal-serif kt-journal-faq__heading';
      heading.textContent = titleText;
      section.appendChild(heading);

      items.forEach(({ question, answerHtml }) => {
        const details = document.createElement('details');
        details.className = 'kt-toggle kt-toggle--faq';
        details.innerHTML = `
          <summary class="kt-toggle__title kt-text-size--medium">
            <div class="kt-toggle__heading">
              <span class="kt-toggle__heading-text">${this.escapeHtml(question)}</span>
            </div>
            <span class="kt-toggle__icon">
              <span class="kt-toggle__icon--dropdown">
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="6" viewBox="0 0 11 6" fill="none" aria-hidden="true">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.58938 5.69611C6.24767 6.03782 5.69365 6.03782 5.35194 5.69611L1.19769 1.54186C0.855979 1.20015 0.855979 0.64613 1.19769 0.304421C1.5394 -0.0372873 2.09342 -0.0372873 2.43512 0.304421L5.97066 3.83996L9.50619 0.304421C9.8479 -0.0372876 10.4019 -0.0372876 10.7436 0.304421C11.0853 0.64613 11.0853 1.20015 10.7436 1.54186L6.58938 5.69611Z" fill="#181817"/>
                </svg>
              </span>
            </span>
          </summary>
          <div class="kt-toggle__content kt-rte kt-remove-empty-space kt-spacing--xsmall kt-gutter-bottom--regular kt-gutter-top--xsmall">${answerHtml}</div>
        `;
        section.appendChild(details);
      });

      return section;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  customElements.define('article-faq-from-content', ArticleFaqFromContent);
}
